import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, Lock, Edit2, Eye, EyeOff, Calendar, Users, LogOut, Trash2, AlertTriangle } from 'lucide-react';
import Cropper, { Area } from 'react-easy-crop';
import Modal from 'react-modal';
import { getDownloadURL, ref, uploadBytesResumable } from '@firebase/storage';
import { storage } from '../firebase';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useUserData } from '../lib/contexts/UserContext';
import { useHospDocData } from '../lib/contexts/HospitalDoctorContext';
import { DocProfileData } from '../services/types';
import { updateDoctor } from '../lib/apis/doctor';
import PasswordModal from '../components/organisms/modals/PasswordModal';
import { modalStyles } from '../assets/styles/modalStyles';
import { sultanHospitals } from '../lib/utils/constants';
import Header from '../components/organisms/Header';
import { useNavigate } from 'react-router-dom';
import ProfileSkeleton from '../components/atoms/skeletons/ProfileSkeleton';

const Profile: React.FC = () => {
    const { userData, handleLogout, handleSultanLogout } = useUserData();
    const { doctorsData, hospitalID } = useHospDocData();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [profileData, setProfileData] = useState<DocProfileData | null>(null);
    const [originalProfileData, setOriginalProfileData] = useState<DocProfileData | null>(null);

    // Image upload/crop states
    const [imgSrc, setImgSrc] = useState<string | ArrayBuffer | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | undefined>();
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Set doctor profile from context
    useEffect(() => {
        if (doctorsData && userData?.doctor_id) {
            const doc = doctorsData.find(d => d.doctor_id === userData.doctor_id);
            if (doc) {
                setProfileData(doc as DocProfileData);
                setOriginalProfileData(doc as DocProfileData);
            }
        }
    }, [doctorsData, userData?.doctor_id]);

    const handleInputChange = (field: keyof DocProfileData, value: any): void => {
        setProfileData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    };

    // Gender button toggle
    const handleGenderChange = (gender: 'Male' | 'Female' | 'Other') => {
        setProfileData(prev => prev ? { ...prev, gender } : prev);
    };

    const handleRemoveImage = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        setProfileData((prev) => (prev ? { ...prev, profile_picture: profileData?.full_name } : prev));
    };

    // Image upload/crop logic
    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImgSrc(reader.result);
                setCropModalOpen(true);
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const uploadCroppedImage = async () => {
        if (croppedAreaPixels && imgSrc && profileData) {
            const imageSrc = imgSrc as string;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const image = new window.Image();
            image.src = imageSrc;
            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;
            image.onload = () => {
                if (ctx) {
                    ctx.drawImage(
                        image,
                        croppedAreaPixels.x,
                        croppedAreaPixels.y,
                        croppedAreaPixels.width,
                        croppedAreaPixels.height,
                        0,
                        0,
                        croppedAreaPixels.width,
                        croppedAreaPixels.height
                    );
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            setUploadStatus('Canvas is empty');
                            return;
                        }
                        const file = new File([
                            blob
                        ], profileData.full_name.replace(/\s/g, '_') + '_profile.png', { type: 'image/png' });
                        const storageRef = ref(storage, `doc_profile_pictures/${file.name}`);
                        const uploadTask = uploadBytesResumable(storageRef, file);
                        uploadTask.on(
                            'state_changed',
                            (snapshot) => {
                                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                setUploadStatus(`Upload is ${progress.toFixed(2)}% done`);
                            },
                            (error) => {
                                setUploadStatus(`Upload failed: ${error.message}`);
                            },
                            () => {
                                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                                    setUploadStatus('File successfully uploaded');
                                    setProfileData((prev) => prev ? { ...prev, profile_picture: downloadURL } : prev);
                                });
                            }
                        );
                    }, 'image/png');
                }
            };
        }
    };

    // Save/cancel logic
    const handleSave = async (): Promise<void> => {
        if (!userData?.doctor_id || !profileData) return;
        try {
            // Only include fields that exist in the doctors table and required by DocProfileData
            const docProfile: DocProfileData = {
                doctor_id: profileData.doctor_id,
                mapping_id: profileData.mapping_id, // required by type, will be omitted before sending
                profile_picture: profileData.profile_picture,
                full_name: profileData.full_name,
                contact_number: profileData.contact_number,
                specialization: profileData.specialization,
                gender: profileData.gender,
                date_of_birth: profileData.date_of_birth,
                experience_years: Number(profileData.experience_years),
                qualification: profileData.qualification,
                is_active: profileData.is_active,
                created_by_user_id: profileData.created_by_user_id,
                updated_by_user_id: profileData.updated_by_user_id,
                created_at: profileData.created_at,
                updated_at: profileData.updated_at,
                gmeet_link: profileData.gmeet_link,
                physical_amount: profileData.physical_amount,
                video_amount: profileData.video_amount,
                pre_payment: profileData.pre_payment
            };
            // Omit mapping_id before sending to backend
            const { mapping_id, ...toSend } = docProfile;
            const res = await updateDoctor(userData.doctor_id, toSend as any);
            if (res?.status === 200) {
                setIsEditing(false);
                setOriginalProfileData(profileData);
                toast.success('Profile updated successfully!');
            } else {
                toast.error('Failed to update profile');
            }
        } catch (e) {
            toast.error('Error updating profile');
        }
    };

    const navigate = useNavigate();

    const handleCancel = (): void => {
        setIsEditing(false);
        setProfileData(originalProfileData);
    };

    const handleViewQueue = (): void => {
        // Navigate to queue page
        navigate(`/${hospitalID}/doctors/doc/${profileData?.mapping_id}`);
        console.log('Navigating to queue');
    };

    const handleViewCalendar = (): void => {
        // Navigate to calendar page
        navigate(`/${hospitalID}/doctors/${profileData?.mapping_id}`);
        console.log('Navigating to calendar');
    };

    const handleLogoutClick = () => {
        if (sultanHospitals.includes(hospitalID || "")) {
            handleSultanLogout();
        } else {
            handleLogout();
        }
    };

    if (!profileData) {
        return <ProfileSkeleton />;
    }

    return (
        <div className="flex flex-col w-full h-[100vh] overflow-y-auto side_bar bg-gray-50">
            <Header header value="Profile" />
            <div className="flex-1 p-6">
                <div className="mx-auto">
                    {/* Top Logout and Change Password Buttons */}
                    <div className="flex justify-end mb-6 gap-2">
                        <button
                            onClick={() => setPasswordModalOpen(true)}
                            className="flex items-center space-x-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            <Lock className="w-5 h-5" />
                            <span>Change Password</span>
                        </button>
                        <button
                            onClick={handleLogoutClick}
                            className="flex items-center space-x-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>
                    </div>
                    <PasswordModal
                        modalIsOpen={passwordModalOpen}
                        closeModal={() => setPasswordModalOpen(false)}
                        customStyles={modalStyles}
                    />
                    {/* Profile Header Card */}
                    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 rounded-lg shadow-sm border border-gray-200 p-6 mb-6 relative overflow-hidden">
                        {/* Gradient overlay effect */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-300/15 to-transparent rounded-full -ml-12 -mb-12"></div>
                        <div className=" z-10">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-0 justify-between mb-6">
                                <div className="flex items-center space-x-4">
                                    <div className="relative">
                                        {
                                            profileData.profile_picture !== null && profileData.profile_picture !== profileData.full_name ? <img
                                                src={profileData.profile_picture}
                                                alt="Profile"
                                                className="w-12 h-12 md:w-20 md:h-20 rounded-full object-cover border-4 border-blue-100"
                                            /> : <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
                                                {profileData.full_name ? profileData.full_name.substring(0, 2).toUpperCase() : "DR"}
                                            </div>
                                        }

                                        <div className={`absolute -bottom-0 -right-0 w-5 h-5 rounded-full border-2 border-white ${profileData.is_active === true ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                        {isEditing && (
                                            <>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={onSelectFile}
                                                    className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
                                                    style={{ zIndex: 2 }}
                                                />
                                                <span className="absolute left-0 bottom-0 bg-blue-600 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg z-10">Change</span>
                                                {profileData.profile_picture && (
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveImage}
                                                        className="absolute -top-1 -right-1 bg-red-600 text-white w-6 h-6 flex items-center justify-center rounded-full z-20 border-2 border-white"
                                                        aria-label="Remove profile picture"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-800">{`Dr. ${profileData.full_name}`}</h2>
                                        <p className="text-blue-600 font-medium">{profileData.specialization}</p>
                                        <p className="text-gray-500">{profileData.qualification}</p>
                                    </div>
                                </div>
                                <div className="flex md:flex-wrap lg:justify-between gap-1 md:gap-3">
                                    {!isEditing ? (
                                        <>
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className="flex items-center space-x-2 px-3 md:px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium w-full md:w-auto justify-center"
                                            >
                                                <Edit2 className="hidden md:block w-5 h-5" />
                                                <span className='hidden md:block'>Edit Profile</span>
                                                <span className='block md:hidden'>Edit</span>
                                            </button>
                                            <button
                                                onClick={handleViewQueue}
                                                className="flex items-center space-x-2 px-3 md:px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium w-full md:w-auto justify-center"
                                            >
                                                <Users className="hidden md:block w-5 h-5" />
                                                <span className='hidden md:block'>View Queue</span>
                                                <span className='block md:hidden'>Queue</span>
                                            </button>
                                            <button
                                                onClick={handleViewCalendar}
                                                className="flex items-center space-x-2 px-3 md:px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium w-full md:w-auto justify-center"
                                            >
                                                <Calendar className="hidden md:block w-5 h-5" />
                                                <span className='hidden md:block'>View Calendar</span>
                                                <span className='block md:hidden'>Calendar</span>
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={handleSave}
                                                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={handleCancel}
                                                className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cropping Modal */}
                    <Modal
                        isOpen={cropModalOpen}
                        onRequestClose={() => setCropModalOpen(false)}
                        ariaHideApp={false}
                        style={{ content: { width: '400px', height: '400px', margin: 'auto' } }}
                    >
                        <div className="flex flex-col items-center justify-center h-full">
                            {imgSrc && (
                                <div className="relative w-72 h-72 bg-gray-100">
                                    <Cropper
                                        image={String(imgSrc)}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={1 / 1}
                                        onCropChange={setCrop}
                                        onCropComplete={onCropComplete}
                                        onZoomChange={setZoom}
                                    />
                                </div>
                            )}
                            <div className="flex mt-4 space-x-2">
                                <button
                                    onClick={() => {
                                        uploadCroppedImage();
                                        setCropModalOpen(false);
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Crop & Upload
                                </button>
                                <button
                                    onClick={() => setCropModalOpen(false)}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                            {uploadStatus && <p className="mt-2 text-sm">{uploadStatus}</p>}
                        </div>
                    </Modal>

                    {/* Profile Details Card */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-6">Profile Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Full Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <User className="w-4 h-4 inline mr-2" />
                                    Full Name
                                </label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={profileData.full_name}
                                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800 font-medium">{`Dr. ${profileData.full_name}`}</p>
                                )}
                            </div>
                            {/* Date of Birth */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date of Birth
                                </label>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        value={profileData.date_of_birth}
                                        min="1954-01-01"
                                        max={moment().format('YYYY-MM-DD')}
                                        onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800">{moment(profileData.date_of_birth).format('D MMMM YYYY')}</p>
                                )}
                            </div>
                            {/* Gender */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                                {isEditing ? (
                                    <div className="flex flex-row space-x-3">
                                        <button
                                            className={`px-4 py-2 rounded-[20px] ${profileData.gender === 'Male' ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-600 hover:bg-blue-100'}`}
                                            onClick={() => handleGenderChange('Male')}
                                            type="button"
                                        >
                                            Male
                                        </button>
                                        <button
                                            className={`px-4 py-2 rounded-[20px] ${profileData.gender === 'Female' ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-600 hover:bg-blue-100'}`}
                                            onClick={() => handleGenderChange('Female')}
                                            type="button"
                                        >
                                            Female
                                        </button>
                                        <button
                                            className={`px-4 py-2 rounded-[20px] ${profileData.gender === 'Other' ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-600 hover:bg-blue-100'}`}
                                            onClick={() => handleGenderChange('Other')}
                                            type="button"
                                        >
                                            Other
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-gray-800">{profileData.gender}</p>
                                )}
                            </div>
                            {/* Experience Years */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Experience Years</label>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        min="0"
                                        value={profileData.experience_years}
                                        onChange={(e) => handleInputChange('experience_years', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800">{profileData.experience_years}</p>
                                )}
                            </div>
                            {/* Specialization */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={profileData.specialization}
                                        onChange={(e) => handleInputChange('specialization', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-blue-600 font-medium">{profileData.specialization}</p>
                                )}
                            </div>
                            {/* Qualification */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Qualification</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={profileData.qualification}
                                        onChange={(e) => handleInputChange('qualification', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800">{profileData.qualification}</p>
                                )}
                            </div>
                            {/* Contact Number */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Phone className="w-4 h-4 inline mr-2" />
                                    Contact Number
                                </label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        value={profileData.contact_number}
                                        onChange={(e) => handleInputChange('contact_number', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800">+{profileData.contact_number}</p>
                                )}
                            </div>
                            {/* Google Meet Link */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Google Meet Link</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={profileData.gmeet_link}
                                        onChange={(e) => handleInputChange('gmeet_link', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <p className="text-gray-800">{profileData.gmeet_link}</p>
                                )}
                            </div>
                            {/* Login Email */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Mail className="w-4 h-4 inline mr-2" />
                                    Login Email
                                </label>
                                <p className="text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">{profileData.email}</p>
                                {/* <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p> */}
                            </div>
                            {/* Login Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Lock className="w-4 h-4 inline mr-2" />
                                    Login Password
                                </label>
                                <p className="text-gray-800">••••••••••••</p>
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <div className="flex items-center space-x-2">
                                    <div className={`w-3 h-3 rounded-full ${profileData.is_active ? 'bg-green-500' : 'bg-gray-400'
                                        }`}></div>
                                    <span className="text-gray-800">{profileData.is_active ? 'Online' : 'Offline'}</span>
                                </div>
                            </div>
                        </div>
                        {uploadStatus && <p className="mt-2 text-sm text-blue-600">{uploadStatus}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;